import { matches, whenText, type DashRfq, type RfqPhase, t } from '@procumi/shared';
import { useMemo, useState, type ReactNode } from 'react';
import { I, money, Section } from './shared';

/** Starea de pe rând: faza cererii, cu „Oferte primite” despărțit de „Așteaptă”. */
export type RowStatus = 'draft' | 'waiting' | 'received' | 'late' | 'ready' | 'order';

export const STATUS: Record<RowStatus, { text: string; tone: string; rank: number }> = {
  late: { text: t('Întârziat'), tone: 'red', rank: 0 },
  ready: { text: t('Comparație gata'), tone: 'blue', rank: 1 },
  order: { text: t('De comandat'), tone: 'night', rank: 2 },
  received: { text: t('Oferte primite'), tone: 'ink', rank: 3 },
  waiting: { text: t('Așteaptă'), tone: 'gray', rank: 4 },
  draft: { text: t('Netrimisă'), tone: 'gray', rank: 5 },
};

export const rowStatus = (phase: RfqPhase, r: DashRfq): RowStatus =>
  phase === 'decide'
    ? 'ready'
    : phase === 'order'
      ? 'order'
      : phase === 'late'
        ? 'late'
        : phase === 'draft'
          ? 'draft'
          : r.offers
            ? 'received'
            : 'waiting';

type Key = 'title' | 'site' | 'status' | 'responses' | 'value' | 'deadline';
const FILTERS: { key: RowStatus | ''; label: string }[] = [
  { key: '', label: t('Toate') },
  { key: 'late', label: t('Întârziate') },
  { key: 'ready', label: t('Comparație gata') },
  { key: 'received', label: t('Oferte primite') },
  { key: 'waiting', label: t('Așteaptă') },
  { key: 'order', label: t('De comandat') },
];

export interface RfqRow {
  r: DashRfq;
  status: RowStatus;
  /** butonul din coloana „Acțiune” */
  action: ReactNode;
}

/**
 * „Achiziții în lucru”: tabelul de lucru al panoului. Cererile deschise de pe
 * toate șantierele; se caută, se filtrează după stare și șantier, se sortează
 * după orice coloană; rândul deschide cererea, butonul face pasul următor.
 */
export function ActiveRfqsTable({
  rows,
  today,
  sites,
  site,
  onSite,
  onOpen,
  onAll,
}: {
  rows: RfqRow[];
  today: string;
  sites: { id: string; name: string }[];
  site: string;
  onSite: (id: string) => void;
  onOpen: (id: string) => void;
  onAll: () => void;
}) {
  const [sort, setSort] = useState<{ key: Key; dir: 1 | -1 }>({ key: 'status', dir: 1 });
  const [filter, setFilter] = useState<RowStatus | ''>('');
  const [q, setQ] = useState('');

  const counts = useMemo(() => {
    const c: Partial<Record<RowStatus, number>> = {};
    for (const x of rows) c[x.status] = (c[x.status] ?? 0) + 1;
    return c;
  }, [rows]);

  const shown = useMemo(() => {
    const val = (x: RfqRow): string | number => {
      switch (sort.key) {
        case 'title':
          return x.r.title;
        case 'site':
          return x.r.site.name;
        case 'status':
          return STATUS[x.status].rank;
        case 'responses':
          return x.r.sends ? x.r.offers / x.r.sends : 0;
        case 'value':
          return x.r.best?.total ?? -1;
        case 'deadline':
          return x.r.deadline;
      }
    };
    return rows
      .filter(
        (x) =>
          (!filter || x.status === filter) && matches(`${x.r.title} ${x.r.site.name} ${x.r.category}`, q),
      )
      .sort((a, b) => {
        const va = val(a);
        const vb = val(b);
        const c =
          typeof va === 'number' && typeof vb === 'number'
            ? va - vb
            : String(va).localeCompare(String(vb), 'ro');
        return c * sort.dir || a.r.deadline.localeCompare(b.r.deadline);
      });
  }, [rows, sort, filter, q]);

  const th = (key: Key, label: string, cls?: string) => (
    <th className={cls} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => setSort({ key, dir: sort.key === key ? (-sort.dir as 1 | -1) : 1 })}
      >
        {label}
        <span className="db-sort">{sort.key === key ? (sort.dir === 1 ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );

  return (
    <Section
      icon="rfq"
      tone="blue"
      title={t('Achiziții în lucru')}
      sub={t('Cererile de ofertă deschise pe șantierele tale')}
      className="db-rfqs"
      action={
        <button type="button" className="link" onClick={onAll}>
          {t('Toate cererile →')}
        </button>
      }
    >
      <div className="db-rfq-tools">
        <div className="db-chips" role="tablist">
          {FILTERS.map((f) => {
            const n = f.key ? (counts[f.key] ?? 0) : rows.length;
            if (f.key && !n) return null;
            return (
              <button
                key={f.key || 'all'}
                type="button"
                role="tab"
                aria-selected={filter === f.key}
                onClick={() => setFilter(f.key)}
              >
                {f.label} <span>{n}</span>
              </button>
            );
          })}
        </div>
        <div className="db-rfq-f">
          {sites.length > 1 ? (
            <select
              className="db-mini"
              aria-label={t('Șantier')}
              value={site}
              onChange={(e) => onSite(e.target.value)}
            >
              <option value="">{t('Toate șantierele')}</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : null}
          <label className="db-find">
            <I d="search" size={14} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('Caută cerere, material')}
              aria-label={t('Caută în cereri')}
            />
          </label>
        </div>
      </div>
      {shown.length ? (
        <div className="db-table-wrap">
          <table className="db-table db-main">
            <thead>
              <tr>
                {th('title', t('Cerere'))}
                {th('site', t('Șantier'))}
                {th('status', t('Stare'))}
                {th('responses', t('Răspunsuri'))}
                {th('value', t('Cea mai bună ofertă'), 'n')}
                {th('deadline', t('Termen'))}
                <th className="act">
                  <span className="sr-only">{t('Acțiune')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.slice(0, 10).map(({ r, status, action }) => {
                const late =
                  r.deadline < today && (status === 'waiting' || status === 'received' || status === 'late');
                return (
                  <tr key={r.id} onClick={() => onOpen(r.id)}>
                    <td data-l={t('Cerere')} className="t">
                      <b>{r.title}</b>
                      <small>{r.category}</small>
                    </td>
                    <td data-l={t('Șantier')} className="site">
                      {r.site.name}
                    </td>
                    <td data-l={t('Stare')}>
                      <span className={`db-st ${STATUS[status].tone}`}>{STATUS[status].text}</span>
                    </td>
                    <td data-l={t('Răspunsuri')}>
                      <span className="db-offers">
                        <span className="db-pips" aria-hidden="true">
                          {Array.from({ length: Math.min(r.sends, 8) }, (_, i) => (
                            <i key={i} className={i < r.offers ? 'on' : undefined} />
                          ))}
                        </span>
                        {r.offers} / {r.sends}
                      </span>
                    </td>
                    <td data-l={t('Cea mai bună')} className="n v">
                      {r.best ? money(r.best.total) : <span className="muted">—</span>}
                    </td>
                    <td
                      data-l={t('Termen')}
                      className={late ? 'late' : r.deadline === today ? 'soon' : undefined}
                    >
                      {r.deadline === today
                        ? t('Azi')
                        : late
                          ? `${whenText(r.deadline, today)}`
                          : whenText(r.deadline, today).replace(/^în /, 'în ')}
                    </td>
                    <td className="act" onClick={(e) => e.stopPropagation()}>
                      {action}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {shown.length > 10 ? (
            <button type="button" className="link db-more" onClick={onAll}>
              {t('încă {0} în „Cereri de ofertă” →', shown.length - 10)}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="db-empty">
          {rows.length ? t('Nicio cerere nu se potrivește filtrului.') : t('Nicio cerere deschisă.')}
        </div>
      )}
    </Section>
  );
}
